import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Download, History, Package, X } from 'lucide-react';
import * as XLSX from 'xlsx';
import { api } from '../../services/api';
import recordOperationsDownload from '../../utils/recordOperationsDownload';

// Ciclo de dotación: cada entrega vence a los 4 meses exactos y se debe
// volver a entregar — se repite indefinidamente mientras el colaborador
// siga activo (people.php ya deja de listarlo cuando se desactiva, así
// que este módulo no necesita lógica propia para "detener" el ciclo).
const CYCLE_MONTHS = 4;
const SOON_DAYS = 30;

function toYMD(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function addMonths(ymd, months) {
  const [y, m, d] = String(ymd || '').split('-').map(Number);
  if (!y || !m || !d) return null;
  const dt = new Date(y, (m - 1) + months, d);
  return dt;
}

function daysBetween(a, b) {
  const ms = new Date(b.getFullYear(), b.getMonth(), b.getDate()) - new Date(a.getFullYear(), a.getMonth(), a.getDate());
  return Math.round(ms / 86400000);
}

const STATUS_META = {
  vencida: { label: 'Vencida', className: 'bg-red-100 text-red-700 border-red-200' },
  proxima: { label: 'Próxima a vencer', className: 'bg-yellow-100 text-yellow-800 border-yellow-200' },
  al_dia: { label: 'Al día', className: 'bg-green-100 text-green-700 border-green-200' },
  sin_registro: { label: 'Sin registro', className: 'bg-gray-100 text-gray-600 border-gray-200' },
};

export default function DotacionesModule({ people }) {
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [registerTarget, setRegisterTarget] = useState(null); // persona seleccionada
  const [historyTarget, setHistoryTarget] = useState(null);
  const [form, setForm] = useState({ fecha_entrega: toYMD(new Date()), items: '', notes: '' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.hrDotaciones.get({});
      setDeliveries(Array.isArray(res?.data) ? res.data : []);
    } catch (e) {
      setError(e?.message || 'No fue posible cargar el control de dotaciones.');
      setDeliveries([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const deliveriesByDni = useMemo(() => {
    const map = new Map();
    deliveries.forEach((d) => {
      const key = String(d.collaborator_dni || '').replace(/[.\-\s]/g, '');
      if (!key) return;
      const list = map.get(key) || [];
      list.push(d);
      map.set(key, list);
    });
    map.forEach((list) => list.sort((a, b) => String(b.fecha_entrega).localeCompare(String(a.fecha_entrega))));
    return map;
  }, [deliveries]);

  const today = useMemo(() => new Date(), []);

  const rows = useMemo(() => {
    return (Array.isArray(people) ? people : []).map((p) => {
      const key = String(p.id || p.dni || '').replace(/[.\-\s]/g, '');
      const history = deliveriesByDni.get(key) || [];
      const last = history[0] || null;
      let status = 'sin_registro';
      let nextDate = null;
      if (last) {
        nextDate = addMonths(last.fecha_entrega, CYCLE_MONTHS);
        const diff = nextDate ? daysBetween(today, nextDate) : null;
        if (diff === null) status = 'sin_registro';
        else if (diff < 0) status = 'vencida';
        else if (diff <= SOON_DAYS) status = 'proxima';
        else status = 'al_dia';
      }
      return {
        dni: p.id || p.dni || '',
        name: p.name || '-',
        project: p.area || p.project || '',
        lastDate: last?.fecha_entrega || null,
        nextDate: nextDate ? toYMD(nextDate) : null,
        status,
        history,
        person: p,
      };
    });
  }, [people, deliveriesByDni, today]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (statusFilter !== 'all' && r.status !== statusFilter) return false;
      if (q) {
        const hay = `${r.name} ${r.dni} ${r.project}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [rows, search, statusFilter]);

  const stats = useMemo(() => {
    const acc = { total: rows.length, vencida: 0, proxima: 0, al_dia: 0, sin_registro: 0 };
    rows.forEach((r) => { acc[r.status] = (acc[r.status] || 0) + 1; });
    return acc;
  }, [rows]);

  const openRegister = (row) => {
    setRegisterTarget(row);
    setForm({ fecha_entrega: toYMD(new Date()), items: '', notes: '' });
  };

  const submitRegister = async () => {
    if (!registerTarget) return;
    if (!form.fecha_entrega) { window.alert('Selecciona la fecha de entrega.'); return; }
    setSaving(true);
    try {
      await api.hrDotaciones.upsert({
        person_id: registerTarget.person?._dbId || null,
        collaborator_dni: registerTarget.dni,
        collaborator_name: registerTarget.name,
        fecha_entrega: form.fecha_entrega,
        items: form.items.trim() || null,
        notes: form.notes.trim() || null,
      });
      setRegisterTarget(null);
      await load();
    } catch (e) {
      window.alert(e?.message || 'No fue posible registrar la entrega.');
    } finally {
      setSaving(false);
    }
  };

  const removeDelivery = async (id) => {
    if (!window.confirm('¿Eliminar este registro de entrega? Esto recalculará la próxima fecha del colaborador.')) return;
    try {
      await api.hrDotaciones.delete(id);
      await load();
      if (historyTarget) {
        const key = String(historyTarget.dni || '').replace(/[.\-\s]/g, '');
        setHistoryTarget((t) => (t ? { ...t, history: (deliveriesByDni.get(key) || []).filter((d) => d.id !== id) } : t));
      }
    } catch (e) {
      window.alert(e?.message || 'No fue posible eliminar el registro.');
    }
  };

  const exportExcel = () => {
    const ws = XLSX.utils.json_to_sheet(filteredRows.map((r) => ({
      colaborador: r.name,
      documento: r.dni,
      proyecto: r.project,
      ultima_entrega: r.lastDate || '',
      proxima_entrega: r.nextDate || '',
      estado: STATUS_META[r.status].label,
    })));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Dotaciones');
    void recordOperationsDownload({ fileType: 'xlsx', moduleKey: 'recursos-humanos', reportName: 'Control de dotaciones' });
    XLSX.writeFile(wb, `control_dotaciones_${toYMD(new Date())}.xlsx`);
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h4 className="text-base font-semibold text-gray-900">Control de dotaciones</h4>
          <p className="text-xs text-gray-500 mt-0.5">
            Cada entrega vence a los {CYCLE_MONTHS} meses; el ciclo se repite mientras el colaborador esté activo.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nombre o documento..."
            className="border rounded-lg px-3 py-2 text-sm bg-white w-56"
          />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="border rounded-lg px-3 py-2 text-sm bg-white"
          >
            <option value="all">Todos los estados</option>
            <option value="vencida">Vencidas</option>
            <option value="proxima">Próximas a vencer</option>
            <option value="al_dia">Al día</option>
            <option value="sin_registro">Sin registro</option>
          </select>
          <button
            type="button"
            onClick={exportExcel}
            disabled={filteredRows.length === 0}
            className="px-3 py-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-sm inline-flex items-center gap-2 disabled:opacity-50"
          >
            <Download className="h-4 w-4" /> Exportar Excel
          </button>
        </div>
      </div>

      {error && (
        <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <div className="bg-white rounded-2xl p-4 border border-gray-200">
          <div className="text-[11px] text-gray-600 uppercase tracking-wide">Colaboradores</div>
          <div className="text-2xl font-semibold mt-1">{loading ? '...' : stats.total}</div>
        </div>
        <div className="bg-white rounded-2xl p-4 border border-gray-200">
          <div className="text-[11px] text-gray-600 uppercase tracking-wide">Vencidas</div>
          <div className="text-2xl font-semibold mt-1 text-red-700">{loading ? '...' : stats.vencida}</div>
        </div>
        <div className="bg-white rounded-2xl p-4 border border-gray-200">
          <div className="text-[11px] text-gray-600 uppercase tracking-wide">Próximas</div>
          <div className="text-2xl font-semibold mt-1 text-yellow-700">{loading ? '...' : stats.proxima}</div>
          <div className="text-xs text-gray-500 mt-1">{SOON_DAYS} días o menos</div>
        </div>
        <div className="bg-white rounded-2xl p-4 border border-gray-200">
          <div className="text-[11px] text-gray-600 uppercase tracking-wide">Al día</div>
          <div className="text-2xl font-semibold mt-1 text-green-700">{loading ? '...' : stats.al_dia}</div>
        </div>
        <div className="bg-white rounded-2xl p-4 border border-gray-200">
          <div className="text-[11px] text-gray-600 uppercase tracking-wide">Sin registro</div>
          <div className="text-2xl font-semibold mt-1 text-gray-600">{loading ? '...' : stats.sin_registro}</div>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Colaborador</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Proyecto</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Última entrega</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Próxima entrega</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Estado</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-gray-500">Cargando...</td></tr>
              ) : filteredRows.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-gray-500">No hay colaboradores para los filtros actuales.</td></tr>
              ) : (
                filteredRows.map((r) => (
                  <tr key={r.dni} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{r.name}</div>
                      <div className="text-xs text-gray-500">CC: {r.dni}</div>
                    </td>
                    <td className="px-4 py-3 text-gray-700">{r.project || '-'}</td>
                    <td className="px-4 py-3 font-mono text-xs text-gray-700">{r.lastDate || '-'}</td>
                    <td className="px-4 py-3 font-mono text-xs text-gray-700">{r.nextDate || '-'}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-1 rounded-full border ${STATUS_META[r.status].className}`}>
                        {STATUS_META[r.status].label}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2 flex-wrap">
                        <button
                          type="button"
                          onClick={() => openRegister(r)}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-white text-xs font-medium"
                          style={{ background: 'linear-gradient(90deg, #2563EB 0%, #4F46E5 100%)' }}
                        >
                          <Package className="h-3.5 w-3.5" /> Registrar entrega
                        </button>
                        {r.history.length > 0 && (
                          <button
                            type="button"
                            onClick={() => setHistoryTarget(r)}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-xs font-medium text-gray-700"
                          >
                            <History className="h-3.5 w-3.5" /> Historial ({r.history.length})
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {registerTarget && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-start justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl w-full max-w-md my-10">
            <div className="p-4 border-b flex items-center justify-between gap-2">
              <div>
                <div className="text-base font-semibold">Registrar entrega de dotación</div>
                <div className="text-xs text-gray-500">{registerTarget.name} · CC: {registerTarget.dni}</div>
              </div>
              <button type="button" onClick={() => setRegisterTarget(null)} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-4 space-y-3">
              <div>
                <label className="block text-xs text-gray-600 mb-1">Fecha de entrega</label>
                <input
                  type="date"
                  value={form.fecha_entrega}
                  onChange={(e) => setForm((s) => ({ ...s, fecha_entrega: e.target.value }))}
                  className="w-full border rounded-xl px-3 py-2 bg-gray-50"
                />
                <p className="text-xs text-gray-500 mt-1">
                  La próxima entrega quedará calculada para {addMonths(form.fecha_entrega, CYCLE_MONTHS) ? toYMD(addMonths(form.fecha_entrega, CYCLE_MONTHS)) : '-'}.
                </p>
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Ítems entregados (opcional)</label>
                <input
                  value={form.items}
                  onChange={(e) => setForm((s) => ({ ...s, items: e.target.value }))}
                  className="w-full border rounded-xl px-3 py-2 bg-gray-50"
                  placeholder="Ej: 2 camisas, 1 pantalón, botas de seguridad"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Observaciones (opcional)</label>
                <textarea
                  value={form.notes}
                  onChange={(e) => setForm((s) => ({ ...s, notes: e.target.value }))}
                  className="w-full border rounded-xl px-3 py-2 bg-gray-50"
                  rows={3}
                />
              </div>
            </div>
            <div className="p-4 border-t flex items-center justify-end gap-2">
              <button type="button" onClick={() => setRegisterTarget(null)} className="px-4 py-2 rounded-xl bg-gray-200 text-gray-800 hover:bg-gray-300">Cancelar</button>
              <button
                type="button"
                onClick={submitRegister}
                disabled={saving}
                className="px-4 py-2 rounded-xl text-white shadow-sm disabled:opacity-50"
                style={{ background: 'linear-gradient(90deg, #2563EB 0%, #4F46E5 100%)' }}
              >
                {saving ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {historyTarget && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-start justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl w-full max-w-lg my-10">
            <div className="p-4 border-b flex items-center justify-between gap-2">
              <div>
                <div className="text-base font-semibold">Historial de dotaciones</div>
                <div className="text-xs text-gray-500">{historyTarget.name} · CC: {historyTarget.dni}</div>
              </div>
              <button type="button" onClick={() => setHistoryTarget(null)} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-4 space-y-2 max-h-[60vh] overflow-y-auto">
              {(deliveriesByDni.get(String(historyTarget.dni || '').replace(/[.\-\s]/g, '')) || []).map((d) => (
                <div key={d.id} className="border rounded-lg p-3 bg-gray-50">
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-mono text-sm text-gray-900">{d.fecha_entrega}</div>
                    <button type="button" onClick={() => removeDelivery(d.id)} className="text-xs text-red-600 hover:text-red-700">Eliminar</button>
                  </div>
                  {d.items && <div className="text-xs text-gray-700 mt-1">Ítems: {d.items}</div>}
                  {d.notes && <div className="text-xs text-gray-600 mt-1">{d.notes}</div>}
                  {d.created_by_name && <div className="text-[11px] text-gray-400 mt-1">Registrado por {d.created_by_name}</div>}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
